import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Eye, EyeOff, ShieldCheck, Hexagon, KeyRound, ScanLine } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../../contexts/AuthContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";

const schema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
  rememberDevice: z.boolean().optional(),
});

const newPasswordSchema = z.object({
  newPassword: z.string().min(8, "Minimum 8 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

const mfaSchema = z.object({
  otpCode: z.string().length(6, "Enter the 6-digit code"),
});

export default function LoginPage() {
  const { login, confirmNewPassword, confirmMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  // "login" | "new_password" | "totp_setup" | "mfa"
  const [step, setStep] = useState("login");
  const [serverError, setServerError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [totpSetupUri, setTotpSetupUri] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);

  const loginForm = useForm({ resolver: zodResolver(schema), defaultValues: { rememberDevice: true } });
  const newPasswordForm = useForm({ resolver: zodResolver(newPasswordSchema) });
  const mfaForm = useForm({ resolver: zodResolver(mfaSchema) });

  const onLoginSubmit = async (data) => {
    console.log('[LoginPage] onLoginSubmit called', data.email);
    setServerError(null);
    try {
      const result = await login(data);
      const nextStep = result?.nextStep?.signInStep;
      if (nextStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setStep("new_password");
      } else if (nextStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
        const details = result.nextStep.totpSetupDetails;
        setTotpSecret(details.sharedSecret);
        setTotpSetupUri(details.getSetupUri("TETRA MICRO INFUSION SYSTEM", data.email).toString());
        setStep("totp_setup");
      } else if (
        nextStep === "CONFIRM_SIGN_IN_WITH_SMS_MFA" ||
        nextStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
      ) {
        setStep("mfa");
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setServerError(err?.message || "Sign-in failed. Check your credentials and try again.");
    }
  };

  const onNewPasswordSubmit = async (data) => {
    setServerError(null);
    try {
      const result = await confirmNewPassword(data.newPassword);
      const nextStep = result?.nextStep?.signInStep;
      if (nextStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
        const details = result.nextStep.totpSetupDetails;
        setTotpSecret(details.sharedSecret);
        // email not available here, use generic account name
        setTotpSetupUri(details.getSetupUri("TETRA MICRO INFUSION SYSTEM", "user").toString());
        setStep("totp_setup");
      } else if (nextStep === "CONFIRM_SIGN_IN_WITH_SMS_MFA" || nextStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
        setStep("mfa");
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setServerError(err?.message || "Failed to set new password. Please try again.");
    }
  };

  const onMfaSubmit = async (data) => {
    setServerError(null);
    try {
      await confirmMfa(data.otpCode);
      navigate(from, { replace: true });
    } catch (err) {
      setServerError(err?.message || "Invalid code. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">

      {/* Brand */}
      <div className="text-center mb-5">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-teal-800 text-teal-300 mb-3 shadow-lg">
          <Hexagon className="h-8 w-8" />
        </div>
        <h1 className="text-2xl font-bold text-teal-900 tracking-tight">
          TETRA MICRO INFUSION SYSTEM
        </h1>
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mt-1">
          Fleet Management Portal
        </p>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-white rounded-xl shadow-md px-8 py-7">

        {/* ── STEP: Login ─────────────────────────────────────────────────── */}
        {step === "login" && (
          <>
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Sign in to your account</h2>

            {serverError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} noValidate className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="admin@emedtc.com"
                  {...loginForm.register("email")}
                  aria-invalid={!!loginForm.formState.errors.email}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-red-500">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    className="pr-10"
                    {...loginForm.register("password")}
                    aria-invalid={!!loginForm.formState.errors.password}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-red-500">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 accent-teal-700"
                    {...loginForm.register("rememberDevice")}
                  />
                  Remember this device
                </label>
                <button type="button" className="text-sm text-teal-700 hover:underline">
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loginForm.formState.isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-teal-800 hover:bg-teal-900 disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
              >
                {loginForm.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Sign In
              </button>
            </form>

          </>
        )}

        {/* ── STEP: Set New Password ───────────────────────────────────────── */}
        {step === "new_password" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                <KeyRound className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Set a new password</h2>
                <p className="text-xs text-slate-400">Your temporary password has expired. Please set a permanent password.</p>
              </div>
            </div>

            {serverError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={newPasswordForm.handleSubmit(onNewPasswordSubmit)} noValidate className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="newPassword" className="text-sm font-medium text-slate-700">
                  New password <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    autoComplete="new-password"
                    className="pr-10"
                    {...newPasswordForm.register("newPassword")}
                    aria-invalid={!!newPasswordForm.formState.errors.newPassword}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowNewPassword((v) => !v)}
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {newPasswordForm.formState.errors.newPassword && (
                  <p className="text-xs text-red-500">{newPasswordForm.formState.errors.newPassword.message}</p>
                )}
                <p className="text-xs text-slate-400">Min 8 characters &middot; uppercase &middot; number &middot; special character</p>
              </div>

              <div className="space-y-1">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                  Confirm password <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  {...newPasswordForm.register("confirmPassword")}
                  aria-invalid={!!newPasswordForm.formState.errors.confirmPassword}
                />
                {newPasswordForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-500">{newPasswordForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={newPasswordForm.formState.isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-teal-800 hover:bg-teal-900 disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
              >
                {newPasswordForm.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Set Password &amp; Continue
              </button>
            </form>
          </>
        )}

        {/* ── STEP: TOTP Setup (scan QR code) ─────────────────────────────── */}
        {step === "totp_setup" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                <ScanLine className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Set up Authenticator App</h2>
                <p className="text-xs text-slate-400">One-time setup required before you can sign in.</p>
              </div>
            </div>

            {serverError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <ol className="text-sm text-slate-600 space-y-1.5 mb-4 list-decimal list-inside">
              <li>Install <strong>Google Authenticator</strong> or <strong>Authy</strong> on your phone</li>
              <li>Tap <strong>+</strong> → <strong>Scan a QR code</strong></li>
              <li>Scan the code below</li>
              <li>Enter the 6-digit code shown in the app</li>
            </ol>

            {totpSetupUri && (
              <div className="flex flex-col items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4">
                <QRCodeSVG value={totpSetupUri} size={180} level="M" />
                <p className="text-[11px] text-slate-400 text-center">Can't scan? Enter this key manually:</p>
                <code className="text-xs font-mono bg-white border border-slate-200 rounded px-2 py-1 text-slate-700 break-all text-center select-all">
                  {totpSecret}
                </code>
              </div>
            )}

            <form onSubmit={mfaForm.handleSubmit(onMfaSubmit)} noValidate className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="otpCodeSetup" className="text-sm font-medium text-slate-700">
                  6-digit code from app <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="otpCodeSetup"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  className="text-center text-lg tracking-widest font-mono"
                  {...mfaForm.register("otpCode")}
                  aria-invalid={!!mfaForm.formState.errors.otpCode}
                />
                {mfaForm.formState.errors.otpCode && (
                  <p className="text-xs text-red-500">{mfaForm.formState.errors.otpCode.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={mfaForm.formState.isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-teal-800 hover:bg-teal-900 disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
              >
                {mfaForm.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Verify &amp; Complete Setup
              </button>
            </form>
          </>
        )}

        {/* ── STEP: MFA OTP ────────────────────────────────────────────────── */}
        {step === "mfa" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                <ShieldCheck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Verify your identity</h2>
                <p className="text-xs text-slate-400">Enter the 6-digit code sent to your registered device.</p>
              </div>
            </div>

            {serverError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={mfaForm.handleSubmit(onMfaSubmit)} noValidate className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="otpCode" className="text-sm font-medium text-slate-700">
                  One-time code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="otpCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  className="text-center text-lg tracking-widest font-mono"
                  {...mfaForm.register("otpCode")}
                  aria-invalid={!!mfaForm.formState.errors.otpCode}
                />
                {mfaForm.formState.errors.otpCode && (
                  <p className="text-xs text-red-500">{mfaForm.formState.errors.otpCode.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={mfaForm.formState.isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-teal-800 hover:bg-teal-900 disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
              >
                {mfaForm.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Verify &amp; Sign In
              </button>

              <button
                type="button"
                onClick={() => { setStep("login"); setServerError(null); }}
                className="w-full text-sm text-slate-500 hover:text-slate-800 text-center"
              >
                &#8592; Back to sign in
              </button>
            </form>
          </>
        )}
      </div>

    </div>
  );
}
